import React, { useContext } from "react";
import "./case-header.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { icons } from "../../../../../statics/icons";
import { CaseContext } from "../../../../../store";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../../helpers";
import { ResponsablePersonComponent } from "../../../../../components/responsable-person/responsable-person.component";

const CaseHeaderComponent = () => {
  const { t } = useTranslation();
  const { caseDetails, loading } = useContext(CaseContext);

  return (
    <Box className="content">
      <Box className="top__content mb-4">
        <Box className="case_details_content global__box__content px-4 py-3">
          {/* Case Title */}
          {loading ? (
            <Skeleton variant="text" height={20} width={350} className="mb-1" />
          ) : (
            <Typography variant="h1" color="textPrimary" className="mb-1">
              {caseDetails.title}
            </Typography>
          )}

          {/* Case Last Modified By */}
          {loading ? (
            <Skeleton variant="text" height={20} width={200} className="my-2" />
          ) : (
            <Typography
              variant="caption"
              className="my-2 text-capitalize"
              style={{ display: "block", fontWeight: 400 }}
            >
              {t("LAST_MODIFIED_BY")} {caseDetails?.modifiedBy?.name},{" "}
              {formatDate(caseDetails?.updatedAt, "DD.MM.YYYY")}
            </Typography>
          )}

          {/* Case Brief */}
          {loading ? (
            <>
              <Skeleton
                variant="text"
                height={18}
                width="100%"
                style={{ marginBottom: "2px" }}
              />
              <Skeleton
                variant="text"
                height={18}
                width="90%"
                style={{ marginBottom: "2px" }}
              />
              <Skeleton
                variant="text"
                height={18}
                width="70%"
                style={{ marginBottom: "16px" }}
              />
            </>
          ) : (
            <Typography variant="body1" className="mb-4">
              {caseDetails?.brief}
            </Typography>
          )}

          {/* Case Application Areas & Operation Areas */}
          <Box className="application__area">
            {/* Case Application Areas */}
            <Box className="d-flex align-items-center" style={{ gap: "1rem" }}>
              {loading ? (
                <Skeleton variant="text" width={120} height={25} />
              ) : (
                <Typography variant="h3" className="text-capitalize">
                  {t("USECASE_DETAILS.APPLICATION_AREAS")}:
                </Typography>
              )}
              {loading ? (
                <Box className="d-flex" style={{ gap: "10px" }}>
                  <Skeleton
                    variant="rect"
                    height={35}
                    width={100}
                    style={{ borderRadius: "4px" }}
                  />
                  <Skeleton
                    variant="rect"
                    height={35}
                    width={100}
                    style={{ borderRadius: "4px" }}
                  />
                </Box>
              ) : (
                <Box className="items">
                  {caseDetails.areas
                    .slice(0, 2)
                    .map((item: any, index: number) => (
                      <Box key={index} className="item">
                        {item.name}
                      </Box>
                    ))}

                  {caseDetails.areas.length > 2 && (
                    <Box className="item">+{caseDetails.areas.length - 2}</Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Case Operation Areas */}
            <Box className="d-flex align-items-center" style={{ gap: "1rem" }}>
              {loading ? (
                <Skeleton variant="text" width={120} height={25} />
              ) : (
                <Typography variant="h3" className="text-capitalize">
                  {t("USECASE_DETAILS.OPERATION_AREAS")}:
                </Typography>
              )}

              {loading ? (
                <Box className="d-flex" style={{ gap: "10px" }}>
                  <Skeleton
                    variant="rect"
                    height={35}
                    width={100}
                    style={{ borderRadius: "4px" }}
                  />
                  <Skeleton
                    variant="rect"
                    height={35}
                    width={100}
                    style={{ borderRadius: "4px" }}
                  />
                </Box>
              ) : (
                <Box className="items">
                  {caseDetails.operations
                    .slice(0, 2)
                    .map((item: any, index: number) => (
                      <Box key={index} className="item">
                        {item.name}
                      </Box>
                    ))}

                  {caseDetails.operations.length > 2 && (
                    <Box className="item">
                      +{caseDetails.operations.length - 2}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box className="case_info">
          <ResponsablePersonComponent
            user={caseDetails.keyContactUser}
            loading={loading}
            editable={false}
            title={t("KEY_CONTACT_USER")}
          />
          <Box className="global__box__content p-3 mt-4 d-flex align-items-center justify-content-between">
            <Box>
              {loading ? (
                <Skeleton variant="text" height={20} width={100} />
              ) : (
                <Typography variant="h3" color="textPrimary">
                  {caseDetails.major.toLocaleString("en-US", {
                    minimumIntegerDigits: 2,
                  })}
                  .
                  {caseDetails.minor.toLocaleString("en-US", {
                    minimumIntegerDigits: 2,
                  })}
                  .
                  {caseDetails.version.toLocaleString("en-US", {
                    minimumIntegerDigits: 2,
                  })}
                </Typography>
              )}
              {loading ? (
                <Skeleton variant="text" height={20} width={150} />
              ) : (
                <Typography
                  variant="h4"
                  color="textSecondary"
                  className="text-capitalize font-weight-medium"
                >
                  {t("VERSION_NUMBER")}
                </Typography>
              )}
            </Box>
            {loading ? (
              <Skeleton variant="text" height={25} width={130} />
            ) : (
              <Typography
                variant="subtitle1"
                color="textPrimary"
                className="text-capitalize font-weight-medium"
              >
                {caseDetails.major
                  ? t("PUBLISHED")
                  : caseDetails.minor
                  ? t("IN_PROGRESS")
                  : t("NEW")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="global__box__content bottom__content">
        <Box
          className="item"
          style={{ flexDirection: "row", gap: "1rem", alignItems: "center" }}
        >
          {loading ? (
            <Skeleton variant="circle" height={40} width={40} />
          ) : (
            <img
              width={40}
              src={
                caseDetails.riskLevel.iconKey
                  ? icons.PAGES.SETTINGS.ICON_OPTIONS[
                      caseDetails.riskLevel.iconKey
                    ]
                  : icons.categoryPrimary
              }
              alt=""
            />
          )}
          <Box>
            {loading ? (
              <Skeleton variant="text" height={25} width={150} />
            ) : (
              <Typography variant="subtitle1" className="font-weight-medium">
                {caseDetails.riskLevel.name}
              </Typography>
            )}
            {loading ? (
              <Skeleton variant="text" height={18} width={100} />
            ) : (
              <Typography
                variant="h4"
                color="textSecondary"
                className="text-capitalize"
              >
                {t("RISK_LEVEL")}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className="item">
          {loading ? (
            <Skeleton variant="text" height={25} width={150} />
          ) : (
            <Typography variant="subtitle1" className="font-weight-medium">
              {caseDetails.product.name}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" height={18} width={100} />
          ) : (
            <Typography
              variant="h4"
              color="textSecondary"
              className="text-capitalize"
            >
              {t("PRODUCT")}
            </Typography>
          )}
        </Box>
        <Box className="item">
          {loading ? (
            <Skeleton variant="text" height={25} width={150} />
          ) : (
            <Typography variant="subtitle1" className="font-weight-medium">
              {caseDetails.product.version}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" height={18} width={100} />
          ) : (
            <Typography
              variant="h4"
              color="textSecondary"
              className="text-capitalize"
            >
              {t("PRODUCT_VERSION")}
            </Typography>
          )}
        </Box>
        <Box className="item">
          {loading ? (
            <Skeleton variant="text" height={25} width={150} />
          ) : (
            <Typography variant="subtitle1" className="font-weight-medium">
              True
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" height={18} width={100} />
          ) : (
            <Typography
              variant="h4"
              color="textSecondary"
              className="text-capitalize"
            >
              Human Interaction
            </Typography>
          )}
        </Box>
        <Box className="item">
          {loading ? (
            <Skeleton variant="text" height={25} width={150} />
          ) : (
            <Typography variant="subtitle1" className="font-weight-medium">
              {caseDetails.role.name}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" height={18} width={100} />
          ) : (
            <Typography
              variant="h4"
              color="textSecondary"
              className="text-capitalize"
            >
              {t("ROLE")}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CaseHeaderComponent;
